import type { BuilderContent } from "@builder.io/sdk-react";

import { projectEntryRegion, scopeRegionId } from "~/hec/builder/entry-regions";

/**
 * The named `uiBlocks` region on the `pdp-inline-content` model (COM-793).
 *
 * One targeted entry drives two PDP positions: its main canvas (`data.blocks`)
 * renders ABOVE the product accordions, as it always has, and this field
 * renders BELOW them — the placement SFRA's "Featured Content Module 2" had.
 * Same targeting, same scheduling, no extra Builder fetch.
 *
 * The field must exist on the model in each Builder space, under exactly this
 * name. Until it does, the region is simply absent.
 */
export const INLINE_CONTENT_REGIONS = {
  belowAccordions: "belowAccordions",
} as const;

/**
 * The below-accordions region of a `pdp-inline-content` entry, projected so it
 * renders through `PrefetchedBuilderContent` like the main canvas does. `null`
 * when there is no entry or the field is missing / empty.
 *
 * The above slot is NOT projected: it keeps receiving the whole entry, and
 * `<Content>` only ever renders `data.blocks`, so this field cannot leak into
 * it.
 *
 * The projection carries a region-scoped id (`<entryId>-belowAccordions`) so
 * Builder's Preview tab doesn't re-fetch the full entry into this slot and
 * show the main canvas twice — see `scopeRegionId`.
 */
export function selectInlineBelowAccordions(
  entry: BuilderContent | null | undefined,
): BuilderContent | null {
  return scopeRegionId(
    projectEntryRegion(entry, INLINE_CONTENT_REGIONS.belowAccordions),
    INLINE_CONTENT_REGIONS.belowAccordions,
  );
}
